import { LoadingState } from '../../../shared/ui/LoadingState.jsx';
import { useEffect, useMemo, useState } from 'react';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { ElementIcon } from '../../nikke-catalog/shared/ElementIcon.jsx';
import { buildRaidSettlement } from '../domain/raidSettlement.js';
import { useRaidSettlement } from '../hooks/useRaidSettlement.js';
import {
  RaidSettlementBossMatrix,
  RaidSettlementBossNikkeUsage,
  RaidSettlementGrowthChart,
  RaidSettlementLevelHistory,
} from './RaidSettlementInsights.jsx';
import { RaidSettlementReport } from './RaidSettlementReport.jsx';
import { siteImageUrl } from '../../../shared/domain/siteImages.js';

function formatEok(value) {
  const number = Number(value || 0);
  if (number >= 10000) {
    const jo = Math.floor(number / 10000);
    const eok = Math.round(number % 10000);
    return eok ? `${jo}조 ${eok.toLocaleString('ko-KR')}억` : `${jo}조`;
  }
  return `${number.toLocaleString('ko-KR', { maximumFractionDigits: 2 })}억`;
}

function formatPercent(value) {
  return `${Number(value || 0).toLocaleString('ko-KR', { maximumFractionDigits: 1 })}%`;
}

function formatCoefficient(value) {
  return Number(value || 1).toLocaleString('ko-KR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatCoefficientRange(boss = {}) {
  const minimum = Number(boss.coefficientMin ?? boss.coefficient ?? 1);
  const maximum = Number(boss.coefficientMax ?? boss.coefficient ?? 1);
  if (Math.abs(maximum - minimum) < 0.005) return `×${formatCoefficient(boss.coefficient)}`;
  return `×${formatCoefficient(minimum)}~${formatCoefficient(maximum)}`;
}

function SettlementElementIcon({ element, className = '' }) {
  const normalizedElement = String(element || '').trim();
  return <ElementIcon element={normalizedElement} title={`${normalizedElement || '속성 미정'} 속성`} className={`raid-settlement-element-icon ${className}`.trim()} />;
}

function SettlementDeck({ deck = [] }) {
  return (
    <div className="raid-settlement-deck" aria-label="사용 덱">
      {(Array.isArray(deck) ? deck : []).slice(0, 5).map((item, index) => (
        <span key={`${item.code || index}-${index}`} title={item.name || item.code}>
          {item.imageUrl ? <img src={siteImageUrl(item.imageUrl)} alt="" loading="lazy" /> : <b>{String(item.name || item.code || '?').slice(0, 1)}</b>}
        </span>
      ))}
    </div>
  );
}

function SettlementHeading({ raid, season, mobile, tab, onTab }) {
  return (
    <>
      <header className={mobile ? 'raid-settlement-mobile-heading' : 'raid-settlement-heading'}>
        <div>

          <h2>{raid.unionName || '유니온'} {season.seasonNo}차 결산</h2>
          <span>현재 유니온 실전 기록 · 보정은 전체 유니온 20레벨 구간 기준</span>
        </div>
        <div className={mobile ? 'raid-settlement-mobile-controls' : 'raid-settlement-season-control'}>
          {!mobile ? (
            <label>
              <span>조회 차수</span>
              <select value={season.seasonNo} onChange={(event) => raid.selectSeason(event.target.value)}>
                {raid.seasons.map((item) => <option key={item.seasonNo} value={item.seasonNo}>{item.seasonNo}차</option>)}
              </select>
            </label>
          ) : (
            <select aria-label="조회 차수" value={season.seasonNo} onChange={(event) => raid.selectSeason(event.target.value)}>
              {raid.seasons.map((item) => <option key={item.seasonNo} value={item.seasonNo}>{item.seasonNo}차</option>)}
            </select>
          )}
          <RefreshIconButton onClick={() => raid.refresh(season.seasonNo)} />
        </div>
      </header>
      <nav className="raid-settlement-view-tabs" aria-label="결산 화면 선택">
        <button className={tab === 'raid' ? 'active' : ''} type="button" onClick={() => onTab('raid')}>레이드 통계</button>
        <button className={tab === 'members' ? 'active' : ''} type="button" onClick={() => onTab('members')}>멤버 성장</button>
      </nav>
    </>
  );
}

function SettlementComment({ raid, season }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(season.settlementComment || '');

  useEffect(() => {
    setDraft(season.settlementComment || '');
    setEditing(false);
    raid.clearCommentNotice();
  }, [season.seasonNo, season.settlementComment]);

  const save = async () => {
    const saved = await raid.saveComment(draft);
    if (saved) setEditing(false);
  };

  return (
    <section className="raid-settlement-comment">
      <header>
        <div><h3>지휘관 결산 코멘트</h3></div>
        {raid.canManageSettlement && !editing ? <button type="button" onClick={() => { raid.clearCommentNotice(); setEditing(true); }}>수정</button> : null}
      </header>
      {editing ? (
        <div className="raid-settlement-comment-editor">
          <textarea maxLength={1000} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="이번 차수 운영 결과와 다음 레이드 참고사항을 입력하세요." />
          <div><small>{draft.length}/1000</small><button className="secondary" type="button" disabled={raid.commentSaving} onClick={() => { setDraft(season.settlementComment || ''); setEditing(false); raid.clearCommentNotice(); }}>닫기</button><button className="primary" type="button" disabled={raid.commentSaving} onClick={save}>{raid.commentSaving ? '저장 중…' : '저장'}</button></div>
        </div>
      ) : <p>{season.settlementComment || '결산 코멘트 없음'}</p>}
      {raid.commentNotice.message ? <small className={`raid-settlement-comment-notice ${raid.commentNotice.type}`}>{raid.commentNotice.message}</small> : null}
    </section>
  );
}

function BossProgressCard({ boss, onOpen }) {
  const hpLabel = boss.isInfinite ? '무한 체력' : `${formatRaidHpEok(boss.remainingHpEok)} / ${formatRaidHpEok(boss.maxHpEok)}`;
  return (
    <button className={`raid-settlement-boss-card ${boss.isCleared ? 'cleared' : ''}`} type="button" onClick={onOpen}>
      <div className="raid-settlement-boss-image" style={{ backgroundImage: boss.imageUrl ? `url(${siteImageUrl(boss.imageUrl)})` : undefined }}>
        <span>{boss.currentPhase || 1}단계</span>
        <em className="raid-settlement-element-badge"><SettlementElementIcon element={boss.element} /></em>
        {boss.isCleared ? <strong>CLEAR</strong> : null}
      </div>
      <div className="raid-settlement-boss-body">
        <div className="raid-settlement-boss-title-row">
          <div className="raid-settlement-boss-title-copy"><small>구간 보정 {formatCoefficientRange(boss)}</small><strong>{bossDisplayName(boss)}</strong></div>
          <span className="raid-mobile-boss-element raid-settlement-mobile-element" aria-label={boss.element || '속성'} title={boss.element || '속성'}><SettlementElementIcon element={boss.element} /></span>
        </div>
        <div className="raid-settlement-hp-line"><i style={{ width: `${Math.max(0, Math.min(100, boss.remainingPercent || 0))}%` }} /></div>
        <div className="raid-settlement-boss-kpis">
          <span><small>총딜</small><b>{formatEok(boss.totalDamageEok)}</b></span>
          <span><small>평균 1타</small><b>{formatEok(boss.averageDamageEok)}</b></span>
          <span><small>최고타</small><b>{formatEok(boss.topDamageEok)}</b></span>
          <span><small>공격</small><b>{boss.attackCount}타{boss.killCount ? ` · 막타 ${boss.killCount}` : ''}</b></span>
        </div>
        <small className="raid-settlement-hp-copy">{hpLabel}</small>
      </div>
    </button>
  );
}

function SettlementBossGrid({ bosses, onSelect, scope, errorMessage }) {
  const sourceText = errorMessage
    ? '보정 통계 조회 실패 · 현재 유니온 기준 임시 계산'
    : scope?.mode === 'cross_union_level_band'
      ? `보정: 전체 유니온 ${scope.unionCount}개 · ${scope.levelBandSize}레벨 구간 기준`
      : '보정: 현재 유니온 평균딜 기준';
  return <section className="raid-settlement-boss-section"><header><div><h3>보스별 결산</h3><span title={errorMessage || sourceText}>{sourceText} · 카드를 누르면 상세 기록을 확인합니다.</span></div></header><div className="raid-settlement-boss-grid">{bosses.map((boss) => <BossProgressCard boss={boss} key={boss.bossNo} onOpen={() => onSelect(boss.bossNo)} />)}</div></section>;
}

function SettlementScatter({ attacks }) {
  const rows = attacks.filter((attack) => attack.charLevel && attack.damageEok > 0);
  if (!rows.length) return <p className="raid-settlement-panel-empty">싱크 레벨이 포함된 공격 기록 없음</p>;
  const width = 560;
  const height = 280;
  const pad = 32;
  const levels = rows.map((row) => row.charLevel);
  const damages = rows.map((row) => row.damageEok);
  const minLevel = Math.min(...levels);
  const maxLevel = Math.max(...levels);
  const maxDamage = Math.max(...damages, 1);
  const x = (value) => pad + ((value - minLevel) / Math.max(1, maxLevel - minLevel)) * (width - pad * 2);
  const y = (value) => height - pad - (value / maxDamage) * (height - pad * 2);
  return <svg className="raid-settlement-scatter" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="싱크 레벨과 공격 딜 분포">{[0, 1, 2, 3, 4].map((index) => <line className="grid" key={index} x1={pad} x2={width - pad} y1={pad + index * ((height - pad * 2) / 4)} y2={pad + index * ((height - pad * 2) / 4)} />)}<line className="axis" x1={pad} x2={width - pad} y1={height - pad} y2={height - pad} /><line className="axis" x1={pad} x2={pad} y1={pad} y2={height - pad} />{rows.map((row, index) => <circle key={`${row.memberKey}-${row.attackNo}-${index}`} cx={x(row.charLevel)} cy={y(row.damageEok)} r="5"><title>{row.nickname} · Lv.{row.charLevel} · {formatEok(row.damageEok)}</title></circle>)}</svg>;
}

function PhaseDistribution({ phases }) {
  return <div className="raid-settlement-phase-distribution">{phases.map((phase) => <div key={phase.phaseNo}><p><b>{phase.phaseNo}단</b><span>{phase.attackCount}타 · {formatEok(phase.damageEok)}</span></p><div><i style={{ width: `${Math.max(0, Math.min(100, phase.percent))}%` }} /></div></div>)}</div>;
}

function BossAttackDetails({ boss, mobile }) {
  if (!boss.attacks.length) return <p className="raid-settlement-panel-empty">실전 기록 없음</p>;
  if (mobile) return <div className="raid-settlement-boss-attack-cards">{boss.attacks.map((attack, index) => <article key={`${attack.memberKey}-${attack.attackNo}-${index}`}><div><b>#{index + 1}</b><strong>{attack.nickname}</strong><em>{formatEok(attack.damageEok)}</em></div><p><span>싱크 {attack.charLevel || '-'}</span><span>{attack.phaseNo}단</span><span>{attack.attackNo}타{attack.isKill ? ' · 막타' : ''}</span></p><SettlementDeck deck={attack.deck} /></article>)}</div>;
  return <div className="raid-settlement-detail-table-wrap"><table className="raid-settlement-detail-table"><thead><tr><th>순위</th><th>닉네임</th><th>싱크 레벨</th><th>단계</th><th>딜량</th><th>조합</th></tr></thead><tbody>{boss.attacks.map((attack, index) => <tr key={`${attack.memberKey}-${attack.attackNo}-${index}`}><td>{index + 1}</td><td><strong>{attack.nickname}</strong></td><td>{attack.charLevel || '-'}</td><td>{attack.phaseNo}단</td><td><b>{formatEok(attack.damageEok)}</b>{attack.isKill ? <small>막타</small> : null}</td><td><SettlementDeck deck={attack.deck} /></td></tr>)}</tbody></table></div>;
}

function BossDetail({ boss, seasonNo, mobile, onBack }) {
  const name = bossDisplayName(boss);
  return <div className={`raid-settlement-boss-detail ${mobile ? 'mobile' : 'desktop'}`}><button className="raid-settlement-back" type="button" onClick={onBack}>‹ 보스 목록</button><section className="raid-settlement-boss-hero" style={{ backgroundImage: boss.imageUrl ? `linear-gradient(90deg, rgba(12, 14, 19, .96), rgba(12, 14, 19, .42)), url(${siteImageUrl(boss.imageUrl)})` : undefined }}><div><span className="raid-settlement-boss-meta"><SettlementElementIcon element={boss.element} /><b>{boss.currentPhase}단계</b><i aria-hidden="true">·</i><b>{seasonNo}차</b><i aria-hidden="true">·</i><b>보정 {formatCoefficientRange(boss)}</b></span><h2>{name}</h2><p>남은 HP <b>{boss.isInfinite ? '∞' : formatPercent(boss.remainingPercent)}</b></p><div className="raid-settlement-hp-line big"><i style={{ width: `${Math.max(0, Math.min(100, boss.remainingPercent || 0))}%` }} /></div><small>{boss.isInfinite ? '무한 체력' : `${formatRaidHpEok(boss.remainingHpEok)} / ${formatRaidHpEok(boss.maxHpEok)}`}</small></div><aside><p><span>공격 수</span><b>{boss.attackCount}타</b></p><p><span>평균 딜</span><b>{formatEok(boss.averageDamageEok)}</b></p><p><span>최고 딜</span><b>{formatEok(boss.topDamageEok)}</b></p></aside></section><section className="raid-settlement-detail-grid"><div className="raid-settlement-detail-panel"><h3>사용 니케 TOP 5</h3><RaidSettlementBossNikkeUsage boss={boss} mobile={mobile} /></div><div className="raid-settlement-detail-panel"><h3>싱크 레벨 × 딜 분포</h3><SettlementScatter attacks={boss.attacks} /></div><div className="raid-settlement-detail-panel"><h3>단계별 공격 분포</h3><PhaseDistribution phases={boss.phaseDistribution} /></div></section><section className="raid-settlement-detail-records"><header><h3>{name} 상세 딜량</h3><span>{boss.attacks.length}타</span></header><BossAttackDetails boss={boss} mobile={mobile} /></section></div>;
}

function RaidStatistics({ raid, season, settlement, mobile, onSelectBoss }) {
  return <><SettlementComment raid={raid} season={season} /><RaidSettlementReport settlement={settlement} /><SettlementBossGrid bosses={settlement.bossTotals} onSelect={onSelectBoss} scope={settlement.correctionScope} errorMessage={raid.correctionError} /><RaidSettlementBossMatrix settlement={settlement} mobile={mobile} /></>;
}

function MemberGrowth({ raid, mobile }) {
  return <><RaidSettlementGrowthChart history={raid.levelHistory} mobile={mobile} /><RaidSettlementLevelHistory history={raid.levelHistory} errorMessage={raid.insightError} mobile={mobile} /></>;
}

export function RaidSettlementView({ mobile = false }) {
  const raid = useRaidSettlement();
  const season = raid.selectedSeason;
  const [tab, setTab] = useState('raid');
  const [selectedBossNo, setSelectedBossNo] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const settlement = useMemo(() => buildRaidSettlement({ records: season?.records, members: season?.members, bosses: season?.bosses, correctionStats: raid.correctionStats, catalog }), [catalog, raid.correctionStats, season]);
  const selectedBoss = settlement.bossTotals.find((boss) => boss.bossNo === selectedBossNo) || null;

  useEffect(() => {
    let active = true;
    loadNikkeCatalog()
      .then((result) => { if (active) setCatalog(result?.lookup || null); })
      .catch(() => { if (active) setCatalog(null); });
    return () => { active = false; };
  }, []);
  useEffect(() => { setSelectedBossNo(null); }, [season?.seasonNo, tab]);
  useEffect(() => { if (selectedBossNo) window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'smooth' })); }, [selectedBossNo]);

  if (raid.loading) return <LoadingState label="결산 데이터 동기화 중" variant="card" />;
  if (raid.errorMessage) return <section className={`raid-settlement-state error ${mobile ? 'mobile' : ''}`}><strong>결산을 불러오지 못했습니다.</strong><p>{raid.errorMessage}</p></section>;
  if (!season) return <section className={`raid-settlement-state ${mobile ? 'mobile' : ''}`}><strong>결산할 레이드 차수가 없습니다.</strong></section>;
  if (selectedBoss) return <section className={`raid-settlement ${mobile ? 'mobile' : 'desktop'}`} aria-label={`${bossDisplayName(selectedBoss)} 상세 결산`}><BossDetail boss={selectedBoss} seasonNo={season.seasonNo} mobile={mobile} onBack={() => setSelectedBossNo(null)} /></section>;

  return <section className={`raid-settlement ${mobile ? 'mobile' : 'desktop'}`} aria-label="유니온 레이드 결산"><SettlementHeading raid={raid} season={season} mobile={mobile} tab={tab} onTab={setTab} />{tab === 'raid' ? <RaidStatistics raid={raid} season={season} settlement={settlement} mobile={mobile} onSelectBoss={setSelectedBossNo} /> : <MemberGrowth raid={raid} mobile={mobile} />}</section>;
}
